import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from './common.js';

export const monthKeySchema = z.string().regex(/^\d{4}-\d{2}$/, 'Неверный формат месяца');

export const receiptStatusSchema = z.enum(['paid', 'partial', 'unpaid', 'unplanned', 'none']);

export const clientMonthExpectedSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  contractId: uuidSchema.nullable(),
  contractLabel: z.string().nullable(),
  monthKey: monthKeySchema,
  amount: z.number().positive('Сумма должна быть больше нуля'),
  note: z.string().max(255).nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type ClientMonthExpectedDto = z.infer<typeof clientMonthExpectedSchema>;

export const clientMonthExpectedListItemSchema = clientMonthExpectedSchema.extend({
  clientName: z.string(),
});
export type ClientMonthExpectedListItemDto = z.infer<typeof clientMonthExpectedListItemSchema>;

export const financeExpectedListResponseSchema = z.object({
  monthKey: monthKeySchema,
  items: z.array(clientMonthExpectedListItemSchema),
  totalExpected: z.number(),
});
export type FinanceExpectedListResponseDto = z.infer<typeof financeExpectedListResponseSchema>;

export const clientPaymentSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  contractId: uuidSchema.nullable(),
  contractLabel: z.string().nullable(),
  receivedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  coversMonth: monthKeySchema,
  amount: z.number().positive('Сумма должна быть больше нуля'),
  method: z.string().max(64).nullable(),
  note: z.string().max(255).nullable(),
  createdById: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type ClientPaymentDto = z.infer<typeof clientPaymentSchema>;

export const clientPaymentListItemSchema = clientPaymentSchema.extend({
  clientName: z.string(),
});
export type ClientPaymentListItemDto = z.infer<typeof clientPaymentListItemSchema>;

export const financePaymentsListResponseSchema = z.object({
  monthKey: monthKeySchema,
  items: z.array(clientPaymentListItemSchema),
  totalReceived: z.number(),
});
export type FinancePaymentsListResponseDto = z.infer<typeof financePaymentsListResponseSchema>;

export const upsertClientMonthExpectedSchema = z.object({
  clientId: uuidSchema,
  monthKey: monthKeySchema,
  amount: z.number().positive('Сумма должна быть больше нуля'),
  contractId: uuidSchema.nullable().optional(),
  note: z.string().max(255).nullable().optional(),
});
export type UpsertClientMonthExpectedDto = z.infer<typeof upsertClientMonthExpectedSchema>;

export const createClientPaymentSchema = z.object({
  clientId: uuidSchema,
  contractId: uuidSchema.nullable().optional(),
  receivedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Неверная дата'),
  coversMonth: monthKeySchema,
  amount: z.number().positive('Сумма должна быть больше нуля'),
  method: z.string().max(64).nullable().optional(),
  note: z.string().max(255).nullable().optional(),
});
export type CreateClientPaymentDto = z.infer<typeof createClientPaymentSchema>;

export const updateClientPaymentSchema = createClientPaymentSchema
  .partial()
  .refine((d) => Object.keys(d).length > 0, { message: 'Нет данных для обновления' });
export type UpdateClientPaymentDto = z.infer<typeof updateClientPaymentSchema>;

export const financeReceiptRowSchema = z.object({
  clientId: uuidSchema,
  clientName: z.string(),
  expected: z.number(),
  expectedId: uuidSchema.nullable(),
  expectedNote: z.string().nullable(),
  received: z.number(),
  remainder: z.number(),
  status: receiptStatusSchema,
  payments: z.array(clientPaymentSchema),
});
export type FinanceReceiptRowDto = z.infer<typeof financeReceiptRowSchema>;

export const financeReceiptsResponseSchema = z.object({
  monthKey: monthKeySchema,
  rows: z.array(financeReceiptRowSchema),
  totals: z.object({
    expected: z.number(),
    received: z.number(),
    remainder: z.number(),
  }),
});
export type FinanceReceiptsResponseDto = z.infer<typeof financeReceiptsResponseSchema>;

export const financeDebtRowSchema = z.object({
  clientId: uuidSchema,
  clientName: z.string(),
  coversMonth: monthKeySchema,
  expected: z.number(),
  received: z.number(),
  remainder: z.number(),
  ageDays: z.number().int().nonnegative(),
});
export type FinanceDebtRowDto = z.infer<typeof financeDebtRowSchema>;

export const financeDebtResponseSchema = z.object({
  rows: z.array(financeDebtRowSchema),
  totalRemainder: z.number(),
});
export type FinanceDebtResponseDto = z.infer<typeof financeDebtResponseSchema>;
